import { useState } from 'react';
import toast from 'react-hot-toast';
import { knowledgeApi } from '../../lib/knowledge-api';
import { useNotifications } from '../../contexts/NotificationContext';

export default function CreateProductModal({ isOpen, onClose, onProductCreated, triggeredFrom }) {
  const { fetchNotifications } = useNotifications();
  const [name, setName] = useState('');
  const [version, setVersion] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  // Generate dynamic message based on what triggered the modal
  const getContextMessage = () => {
    switch (triggeredFrom) {
      case 'createItem':
        return 'You need to create a product before you can create items. Products help organize your knowledge base.';
      case 'createCollection':
        return 'You need to create a product before you can create collections. Products help organize your knowledge base.';
      case 'uploadDocument':
        return 'You need to create a product before you can upload documents. Products help organize your knowledge base.';
      case 'emptySlot':
        return 'You need to create a product before you can add items to collections. Products help organize your knowledge base.';
      case 'chat':
        return 'You need to create at least one product before you can start chatting. Products help organize your knowledge base.';
      case 'notification':
        return 'Your organization has no products yet. Create your first product to start organizing your knowledge base.';
      default:
        return null;
    }
  };

  const contextMessage = getContextMessage();

  const handleSave = async () => {
    // Validate name
    if (!name.trim()) {
      toast.error('Please enter a product name');
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        name: name.trim(),
      };

      // Include version if provided
      if (version.trim()) {
        payload.version = version.trim();
      }

      // Create the product via API
      const response = await knowledgeApi.products.create(payload);

      // Show success message
      toast.success(response.message || 'Product created successfully!');

      // Refresh notifications to clear any "no_products" notifications
      await fetchNotifications({ limit: 50 });

      // Notify parent that a product was created
      if (onProductCreated) {
        onProductCreated(response.product);
      }

      // Reset form and close modal
      setName('');
      setVersion('');
      onClose();
    } catch (error) {
      // Error handling is done by the API client interceptor
      console.error('Failed to create product:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setName('');
    setVersion('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h3 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Create Product</h3>
                    <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 space-y-4">
          {/* Context Message - Why this modal was opened */}
          {contextMessage && (
            <div className="rounded-lg p-4 flex items-start space-x-3" style={{ backgroundColor: 'var(--color-bg-tertiary)', borderWidth: '1px', borderColor: 'var(--color-info)' }}>
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-info)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{contextMessage}</p>
            </div>
          )}

          {/* Product Name Input */}
          <div>
            <label className="text-gray-400 text-sm font-semibold mb-2 block">
              Product Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500"
              placeholder="Enter product name..."
              maxLength={255}
            />
          </div>

          {/* Product Version Input */}
          <div>
            <label className="text-gray-400 text-sm font-semibold mb-2 block">
              Version <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <input
              type="text"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500"
              placeholder="e.g., 1.0.0, v2.3, etc."
              maxLength={50}
            />
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-6 border-t border-gray-700 flex justify-end items-center gap-3">
          <button
            onClick={handleCancel}
            disabled={isLoading}
            className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            style={{ color: 'var(--color-text-on-accent)' }}
          >
            {isLoading && (
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            <span>{isLoading ? 'Creating...' : 'Create Product'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
